/**
 * ============================================================================
 * Iron Meridian - Replay Controls Component
 * ============================================================================
 * 
 * Professional VCR-style controls for turn-by-turn replay.
 * Integrated timeline with turn markers and event highlights.
 */

import { useState } from 'react';

export default function ReplayControls({
  currentTurnIndex,
  totalTurns,
  isPlaying,
  playbackSpeed,
  isAtStart,
  isAtEnd,
  onPlay,
  onPause,
  onStepForward,
  onStepBackward,
  onJumpToStart,
  onJumpToEnd,
  onJumpToTurn,
  onSpeedChange,
  turnHighlights = [] // Array of { turn, type, label } for markers
}) {
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);

  const speeds = [
    { label: '0.5×', value: 2000 },
    { label: '1×', value: 1000 },
    { label: '2×', value: 500 },
    { label: '4×', value: 250 }
  ];

  const currentSpeed = speeds.find(s => s.value === playbackSpeed) || speeds[1];

  const handleTimelineClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const targetTurn = Math.round(percentage * (totalTurns - 1));
    onJumpToTurn(targetTurn);
  };

  return (
    <div className="bg-war-room-800/95 backdrop-blur-sm border-t border-war-room-600/30 p-4 space-y-3">
      {/* Timeline */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>Turn {currentTurnIndex + 1} / {totalTurns}</span>
          <span>Replay Mode</span>
        </div>
        
        {/* Progress Bar with Markers */}
        <div 
          className="relative h-2 bg-war-room-700 rounded-full cursor-pointer group"
          onClick={handleTimelineClick}
        >
          {/* Progress */}
          <div 
            className="absolute left-0 top-0 h-full bg-gradient-to-r from-blue-600 to-purple-600 rounded-full transition-all"
            style={{ width: `${((currentTurnIndex + 1) / totalTurns) * 100}%` }}
          />
          
          {/* Current position marker */}
          <div 
            className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg border-2 border-blue-500 transition-all"
            style={{ left: `${((currentTurnIndex + 1) / totalTurns) * 100}%` }}
          />
          
          {/* Event markers */}
          {turnHighlights.map((highlight, i) => (
            <div
              key={i}
              className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full cursor-pointer hover:scale-150 transition-transform"
              style={{ 
                left: `${(highlight.turn / totalTurns) * 100}%`,
                backgroundColor: 
                  highlight.type === 'surge' ? '#10b981' :
                  highlight.type === 'collapse' ? '#ef4444' :
                  highlight.type === 'combat' ? '#f59e0b' :
                  '#8b5cf6'
              }}
              title={`Turn ${highlight.turn}: ${highlight.label}`}
              onClick={(e) => {
                e.stopPropagation();
                onJumpToTurn(highlight.turn);
              }}
            />
          ))}
          
          {/* Hover tooltip */}
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            <div className="bg-war-room-900 px-2 py-1 rounded text-xs text-white whitespace-nowrap">
              Click to jump to turn
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between gap-4">
        {/* Left: Navigation */}
        <div className="flex items-center gap-2">
          {/* Jump to Start */}
          <button
            onClick={onJumpToStart}
            disabled={isAtStart}
            className="p-2 hover:bg-war-room-700 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Jump to Start"
          >
            <svg className="w-5 h-5 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
            </svg>
          </button>

          {/* Step Backward */}
          <button
            onClick={onStepBackward}
            disabled={isAtStart}
            className="p-2 hover:bg-war-room-700 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Previous Turn"
          >
            <svg className="w-5 h-5 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
            </svg>
          </button>

          {/* Play/Pause */}
          {isPlaying ? (
            <button
              onClick={onPause}
              className="p-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              title="Pause"
            >
              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6zm8 0h4v16h-4z"/>
              </svg>
            </button>
          ) : (
            <button
              onClick={onPlay}
              disabled={isAtEnd}
              className="p-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Play"
            >
              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </button>
          )}

          {/* Step Forward */}
          <button
            onClick={onStepForward}
            disabled={isAtEnd}
            className="p-2 hover:bg-war-room-700 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Next Turn"
          >
            <svg className="w-5 h-5 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
            </svg>
          </button>

          {/* Jump to End */}
          <button
            onClick={onJumpToEnd}
            disabled={isAtEnd}
            className="p-2 hover:bg-war-room-700 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Jump to End"
          >
            <svg className="w-5 h-5 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
              <path d="M7.58 16.89l5.77-4.07c.56-.4.56-1.24 0-1.63L7.58 7.11C6.91 6.65 6 7.12 6 7.93v8.14c0 .81.91 1.28 1.58.82zM16 7v10h2V7h-2z"/>
            </svg>
          </button>
        </div>

        {/* Center: Turn Info */}
        <div className="flex-1 text-center">
          <div className="text-sm text-gray-300 font-medium">
            Turn {currentTurnIndex + 1} of {totalTurns}
          </div>
          <div className="text-xs text-gray-500">
            {isPlaying ? 'Playing' : 'Paused'}
          </div>
        </div>

        {/* Right: Speed Control */}
        <div className="relative">
          <button
            onClick={() => setShowSpeedMenu(!showSpeedMenu)}
            className="px-3 py-2 bg-war-room-700 hover:bg-war-room-600 rounded-lg transition-colors text-sm text-gray-300 flex items-center gap-2"
            title="Playback Speed"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            {currentSpeed.label}
          </button>

          {showSpeedMenu && (
            <>
              {/* Backdrop */}
              <div 
                className="fixed inset-0 z-40"
                onClick={() => setShowSpeedMenu(false)}
              />
              
              {/* Menu */}
              <div className="absolute bottom-full right-0 mb-2 bg-war-room-800 border border-war-room-600/30 rounded-lg shadow-xl overflow-hidden z-50">
                {speeds.map(speed => (
                  <button
                    key={speed.value}
                    onClick={() => {
                      onSpeedChange(speed.value);
                      setShowSpeedMenu(false);
                    }}
                    className={`block w-full px-4 py-2 text-sm text-left hover:bg-war-room-700 transition-colors ${
                      speed.value === playbackSpeed ? 'bg-war-room-700 text-blue-400' : 'text-gray-300'
                    }`}
                  >
                    {speed.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Status Bar */}
      <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
        <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
        <span>Replay Mode Active</span>
      </div>
    </div>
  );
}
